import { ProductType } from '@/data/types/Product';
import type { GA4SearchType } from 'presentation/events/hooks/useTrackSearchSubmit';
import {
	CATEGORY_URL_PATH,
	EVENT_SEARCH_TYPE_KEY,
	KEYWORD_PREFIX,
	LANG_CODE_REGEX,
	PRODUCT_URL_PATH,
	SEARCH_TYPE,
	SPANISH_LANG_PREFIX,
} from 'presentation/events/hooks/useTrackSearchSubmit';

export const emptyToNull = (v?: string | null) => (v?.trim() ? v.trim() : null);

export const getSearchType = (term: string) => {
	if (term.startsWith(KEYWORD_PREFIX)) {
		return SEARCH_TYPE.KEYWORD;
	}
	if (/[A-Z]/.test(term.substring(term.length - 1))) {
		return SEARCH_TYPE.SEARCH_RULE;
	}

	if (/^\d{3,7}\s*\+\s*\d{3,7}$/.test(term.trim())) {
		return SEARCH_TYPE.SKU_PLUS_SKU;
	}

	if (typeof document !== 'undefined') {
		const lang = document.documentElement.lang;
		if (lang && LANG_CODE_REGEX.test(lang) && !lang.toLowerCase().startsWith(SPANISH_LANG_PREFIX)) {
			return SEARCH_TYPE.TRANSLATED;
		}
	}
	return SEARCH_TYPE.REGULAR;
};

export const decodeUrlDestination = (url: string) => {
	try {
		return decodeURI(url);
	} catch {
		return url;
	}
};

export const getURLSearchType = (url: string): GA4SearchType => {
	const stored = sessionStorage.getItem(EVENT_SEARCH_TYPE_KEY) as GA4SearchType | null;
	if (stored !== null) return stored;
	if (url.includes(PRODUCT_URL_PATH)) return SEARCH_TYPE.PRODUCT_REDIRECT;
	if (url.includes(CATEGORY_URL_PATH)) return SEARCH_TYPE.CATEGORY_REDIRECT;
	return SEARCH_TYPE.LANDING_REDIRECT;
};

export const buildSearchPayload = ({
	term,
	products,
	pdpRedirectUrl,
	isNullSearch,
}: {
	term: string;
	products: ProductType[];
	pdpRedirectUrl?: string | null;
	isNullSearch?: boolean;
}) => {
	const storedSearchType = sessionStorage.getItem(EVENT_SEARCH_TYPE_KEY) as GA4SearchType | null;
	if (storedSearchType) {
		sessionStorage.removeItem(EVENT_SEARCH_TYPE_KEY);
	}
	if (isNullSearch) {
		return {
			searchTerm: term,
			searchType: SEARCH_TYPE.NULL_SEARCH,
			urlDestination: decodeUrlDestination(window.location.href),
			numberOfResults: 0,
		};
	}
	if (pdpRedirectUrl) {
		return {
			searchTerm: term,
			searchType: SEARCH_TYPE.PRODUCT_REDIRECT,
			urlDestination: decodeUrlDestination(window.location.origin + pdpRedirectUrl),
			numberOfResults: -1,
		};
	}
	return {
		searchTerm: term,
		searchType: storedSearchType || getSearchType(term),
		urlDestination: decodeUrlDestination(window.location.href),
		numberOfResults: products.length,
	};
};
